import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MultiSelect } from "@/components/ui/multi-select";
import { KanbanCard } from "@/types/kanban";
import { FreelancerStatus } from "@/types/freelancer-rating";
import { TODOS_MOTIVOS, MOTIVOS_STATUS } from "@/constants/motivos-drop";
import { X, AlertCircle } from "lucide-react";

interface DropCandidateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: KanbanCard | null;
  onConfirm: (data: { motivos: string[]; observacoes?: string; status?: FreelancerStatus }) => void;
  isLoading?: boolean;
}

export function DropCandidateModal({
  open,
  onOpenChange,
  candidate,
  onConfirm,
  isLoading = false,
}: DropCandidateModalProps) {
  const [motivosSelecionados, setMotivosSelecionados] = useState<string[]>([]);
  const [observacoes, setObservacoes] = useState<string>("");

  const motivosOptions = TODOS_MOTIVOS.map((motivo) => ({
    label: motivo,
    value: motivo,
  }));

  const handleConfirm = () => {
    if (motivosSelecionados.length === 0) return;
    
    // Verificar se algum dos motivos selecionados é um status de disponibilidade
    // Se houver múltiplos, pegar o primeiro status encontrado
    const motivoComStatus = MOTIVOS_STATUS.find(m => motivosSelecionados.includes(m.motivo));
    
    onConfirm({
      motivos: motivosSelecionados,
      observacoes: observacoes.trim() || undefined,
      status: motivoComStatus?.status,
    });
    setMotivosSelecionados([]);
    setObservacoes("");
  };

  const handleCancel = () => {
    setMotivosSelecionados([]);
    setObservacoes("");
    onOpenChange(false);
  };

  const getInitials = (name: string): string => {
    const words = name.trim().split(' ').filter(word => word.length > 0);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <X className="h-5 w-5" />
            Reprovar Candidato
          </DialogTitle>
          <DialogDescription>
            Informe o motivo da reprovação deste candidato.
          </DialogDescription>
        </DialogHeader>

        {candidate && (
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-linear-to-r from-blue-100 to-purple-100 text-blue-700">
                {getInitials(candidate.freelancer.nome)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-semibold text-slate-900">
                {candidate.freelancer.nome}
              </h4>
              <p className="text-sm text-slate-500">{candidate.freelancer.email}</p>
            </div>
          </div>
        )}

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>
              Motivos da reprovação <span className="text-red-500">*</span>
            </Label>
            <MultiSelect
              options={motivosOptions}
              selected={motivosSelecionados}
              onChange={setMotivosSelecionados}
              placeholder="Selecione um ou mais motivos..."
              disabled={isLoading}
            />
            <p className="text-xs text-slate-500">
              Selecione um ou mais motivos para a reprovação.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações (opcional)</Label>
            <Textarea
              id="observacoes"
              placeholder="Detalhes adicionais sobre a reprovação..."
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              disabled={isLoading}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={motivosSelecionados.length === 0 || isLoading}
          >
            {isLoading ? "Reprovando..." : "Confirmar Reprovação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

